/**
 * Comment popover revealed after the user clicks a thumb.
 *
 * v1.1 single-submit (2026-04-25):
 *   The polarity is *not* submitted on thumb click — submission is deferred
 *   until this popover closes via any path (Send / Cancel / Esc / click-out
 *   / 30s auto-dismiss). The popover guarantees `onCommit` is called exactly
 *   once before `onClose` so the parent always emits a single feedback
 *   record per thumb click. This eliminates the orphan-record problem of
 *   the original eager-submit + second-record-with-comment design and
 *   removes our reliance on the backend's 5s dedup window.
 *
 *   When the backend gains a real PATCH endpoint we can revert to eager
 *   submit + PATCH for richer telemetry; for v1, single-submit is enough.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { Send, X } from "lucide-react";
import { cn } from "@/lib/utils";

interface FeedbackPopoverProps {
  open: boolean;
  /**
   * Called exactly once per popover lifecycle (open → close), regardless
   * of which close path fired. The argument shape mirrors what the parent
   * needs to send a single feedback record for the in-flight thumb click.
   */
  onCommit: (comment: string | undefined, optedOutOfSanitize: boolean) => void;
  /** Called after onCommit has fired, to let the parent drop the popover. */
  onClose: () => void;
  /** Anchor element rect for positioning (top/left). When null, centers in viewport. */
  anchorRect: DOMRect | null;
}

const AUTO_DISMISS_MS = 30_000;

export function FeedbackPopover({ open, onCommit, onClose, anchorRect }: FeedbackPopoverProps) {
  const [comment, setComment] = useState("");
  const [optOut, setOptOut] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const committedRef = useRef(false);
  // Refs mirror state for the auto-dismiss timer (which captures stale state
  // unless we read through a ref).
  const commentRef = useRef("");
  const optOutRef = useRef(false);
  commentRef.current = comment;
  optOutRef.current = optOut;

  // Reset commit-once flag whenever the popover (re)opens.
  useEffect(() => {
    if (open) {
      committedRef.current = false;
      setComment("");
      setOptOut(false);
    }
  }, [open]);

  // Funnel: every close path goes through here so onCommit fires exactly once.
  const closeWithCommit = useCallback(() => {
    if (committedRef.current) {
      onClose();
      return;
    }
    committedRef.current = true;
    const trimmed = commentRef.current.trim();
    onCommit(trimmed.length > 0 ? trimmed : undefined, optOutRef.current);
    onClose();
  }, [onCommit, onClose]);

  // Auto-focus textarea on open
  useEffect(() => {
    if (open && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [open]);

  // Esc to close (commit polarity-only)
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeWithCommit();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, closeWithCommit]);

  // 30s auto-dismiss
  useEffect(() => {
    if (!open) return;
    const id = setTimeout(closeWithCommit, AUTO_DISMISS_MS);
    return () => clearTimeout(id);
  }, [open, closeWithCommit]);

  if (!open) return null;

  // Position: below + slightly right of the anchor; fall back to fixed center.
  const style: React.CSSProperties = anchorRect
    ? {
        position: "fixed",
        top: Math.min(anchorRect.bottom + 6, window.innerHeight - 200),
        left: Math.min(anchorRect.left, window.innerWidth - 320),
      }
    : { position: "fixed", top: "30%", left: "50%", transform: "translateX(-50%)" };

  return (
    <>
      {/* Click-outside backdrop — also commits polarity-only on dismiss. */}
      <button
        type="button"
        aria-label="Fechar feedback"
        onClick={closeWithCommit}
        className="fixed inset-0 z-40 cursor-default"
      />
      <div
        role="dialog"
        aria-label="Adicionar comentário ao feedback"
        style={style}
        className="z-50 w-80 rounded-lg border border-slate-200 bg-white p-3 shadow-xl dark:border-slate-700 dark:bg-slate-900"
      >
        <div className="mb-2 flex items-center justify-between">
          <span className="text-[11px] font-medium text-slate-500 dark:text-slate-400">
            Conte-nos mais (opcional)
          </span>
          <button
            type="button"
            onClick={closeWithCommit}
            aria-label="Fechar"
            className="rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-300"
          >
            <X size={12} />
          </button>
        </div>

        <textarea
          ref={textareaRef}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="O que poderia ser melhor?"
          maxLength={2000}
          rows={3}
          className={cn(
            "w-full resize-none rounded-md border border-slate-200 bg-slate-50 px-2 py-1.5 text-xs leading-relaxed text-slate-700",
            "placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-400",
            "dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:placeholder:text-slate-500",
          )}
        />

        <label className="mt-2 flex items-center gap-1.5 text-[10px] text-slate-500 dark:text-slate-400">
          <input
            type="checkbox"
            checked={optOut}
            onChange={(e) => setOptOut(e.target.checked)}
            className="h-3 w-3"
          />
          <span>Enviar sem ocultar dados (CPF, e-mail)</span>
        </label>

        <div className="mt-2 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={closeWithCommit}
            className="rounded px-2 py-1 text-[11px] text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={closeWithCommit}
            className={cn(
              "flex items-center gap-1 rounded px-2 py-1 text-[11px] transition-colors",
              comment.trim()
                ? "bg-slate-700 text-white hover:bg-slate-600 dark:bg-slate-300 dark:text-slate-900 dark:hover:bg-slate-200"
                : "bg-slate-200 text-slate-500 hover:bg-slate-300 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700",
            )}
          >
            <Send size={10} />
            Enviar
          </button>
        </div>
      </div>
    </>
  );
}
